/**
 * Journal Entry Service
 *
 * API client for double-entry accounting transactions.
 */

import { get, post, patch, del, buildQueryString, type ApiResponse, type PageInfo } from './api-client';
import {
  ENTRY_TYPE_LABELS,
  ENTRY_STATUS_COLORS,
  type EntryType,
  type EntryStatus,
} from '../constants/labels';
import {
  buildPaginationParams,
  buildDateRangeParams,
} from '../utils/api-helpers';
import { getLabel } from '../utils/labels';
import {
  calculateTotalDebits as calcDebits,
  calculateTotalCredits as calcCredits,
  validateJournalEntryLines,
} from '../utils/validators';

import type { Money, MoneyWithRate, AuditInfo } from '../types/common';

// Re-export types for backward compatibility
export type { Money, MoneyWithRate } from '../types/common';
export type { EntryType, EntryStatus } from '../constants/labels';

// ============================================================================
// Types
// ============================================================================

export interface JournalEntryLine {
  id: string;
  lineNumber: number;
  accountId: string;
  accountCode: string;
  accountName: string;
  amount: MoneyWithRate;
  isDebit: boolean;
  memo?: string;
  contactId?: string;
  contactName?: string;
  projectId?: string;
  costCenterId?: string;
  reconciled: boolean;
  reconciliationId?: string;
}

export interface JournalEntry {
  id: string;
  orgId: string;
  entryNumber: string;
  entryDate: string;
  postDate?: string;
  entryType: EntryType;
  status: EntryStatus;
  sourceType?: string;
  sourceId?: string;
  memo?: string;
  reference?: string;
  lines: JournalEntryLine[];
  totalDebit: Money;
  totalCredit: Money;
  // Base currency totals (present when transaction currency differs from base)
  totalDebitBase?: Money;
  totalCreditBase?: Money;
  voidedAt?: string;
  voidedBy?: string;
  voidReason?: string;
  isReversal: boolean;
  reversesEntryId?: string;
  reversedById?: string;
  fiscalPeriodId?: string;
  audit: AuditInfo;
}

export interface CreateJournalEntryLine {
  accountId: string;
  amount: Money;
  isDebit: boolean;
  memo?: string;
  exchangeRate?: string;
  contactId?: string;
  projectId?: string;
  costCenterId?: string;
}

export interface CreateJournalEntryRequest {
  entryDate: string;
  entryType?: EntryType;
  memo?: string;
  reference?: string;
  lines: CreateJournalEntryLine[];
  autoPost?: boolean;
  idempotencyKey: string;
}

export interface UpdateJournalEntryRequest {
  entryDate?: string;
  memo?: string;
  reference?: string;
  lines?: CreateJournalEntryLine[];
}

export interface ListJournalEntriesFilter {
  startDate?: string;
  endDate?: string;
  status?: EntryStatus;
  entryType?: EntryType;
  accountId?: string;
  search?: string;
  /** Server-side sort column (entryDate | entryNumber | status | entryType). */
  sortField?: string;
  /** Sort direction: "asc" | "desc". */
  sortDirection?: string;
}

export interface ListJournalEntriesResponse {
  entries: JournalEntry[];
  pageInfo: PageInfo;
}

export interface LedgerFilter {
  startDate?: string;
  endDate?: string;
}

export interface LedgerEntry {
  journalEntryId: string;
  entryNumber: string;
  entryDate: string;
  memo: string;
  reference: string;
  debit: Money;
  credit: Money;
  runningBalance?: Money;
  reconciled: boolean;
  status: string;
}

export interface GetAccountLedgerResponse {
  entries: LedgerEntry[];
  pageInfo: PageInfo;
  periodDebit: Money;
  periodCredit: Money;
  endingBalance: Money;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Create a new journal entry.
 */
export async function createJournalEntry(
  orgId: string,
  data: CreateJournalEntryRequest
): Promise<ApiResponse<JournalEntry>> {
  return post<JournalEntry>(`/organizations/${orgId}/journal-entries`, data);
}

/**
 * Get journal entry by ID.
 */
export async function getJournalEntry(
  orgId: string,
  entryId: string
): Promise<ApiResponse<JournalEntry>> {
  return get<JournalEntry>(`/organizations/${orgId}/journal-entries/${entryId}`);
}

/**
 * Update a draft journal entry.
 */
export async function updateJournalEntry(
  orgId: string,
  entryId: string,
  data: UpdateJournalEntryRequest
): Promise<ApiResponse<JournalEntry>> {
  return patch<JournalEntry>(`/organizations/${orgId}/journal-entries/${entryId}`, data);
}

/**
 * Post a draft journal entry.
 */
export async function postJournalEntry(
  orgId: string,
  entryId: string,
  idempotencyKey: string
): Promise<ApiResponse<JournalEntry>> {
  return post<JournalEntry>(
    `/organizations/${orgId}/journal-entries/${entryId}:post`,
    { idempotencyKey }
  );
}

/**
 * Void a posted journal entry.
 */
export async function voidJournalEntry(
  orgId: string,
  entryId: string,
  reason: string,
  idempotencyKey: string
): Promise<ApiResponse<JournalEntry>> {
  return post<JournalEntry>(
    `/organizations/${orgId}/journal-entries/${entryId}:void`,
    { reason, idempotencyKey }
  );
}

/**
 * Reverse a posted journal entry.
 */
export async function reverseJournalEntry(
  orgId: string,
  entryId: string,
  reversalDate: string,
  options?: { memo?: string; idempotencyKey?: string }
): Promise<ApiResponse<JournalEntry>> {
  return post<JournalEntry>(
    `/organizations/${orgId}/journal-entries/${entryId}:reverse`,
    { reversalDate, memo: options?.memo, idempotencyKey: options?.idempotencyKey }
  );
}

/**
 * Delete a draft journal entry.
 */
/* v8 ignore next 6 */
export async function deleteJournalEntry(
  orgId: string,
  entryId: string
): Promise<ApiResponse<void>> {
  return del<void>(`/organizations/${orgId}/journal-entries/${entryId}`);
}

/**
 * List journal entries.
 */
export async function listJournalEntries(
  orgId: string,
  filter?: ListJournalEntriesFilter,
  pageSize?: number,
  pageToken?: string
): Promise<ApiResponse<ListJournalEntriesResponse>> {
  const query = buildQueryString({
    ...buildPaginationParams(pageSize, pageToken),
    ...buildDateRangeParams(filter?.startDate, filter?.endDate),
    status: filter?.status,
    entryType: filter?.entryType,
    accountId: filter?.accountId,
    search: filter?.search,
    sortField: filter?.sortField,
    sortDirection: filter?.sortDirection,
  });
  return get<ListJournalEntriesResponse>(`/organizations/${orgId}/journal-entries${query}`);
}

/**
 * Get account ledger (list of transactions for a specific account).
 */
export async function getAccountLedger(
  orgId: string,
  accountId: string,
  filter?: LedgerFilter,
  options?: { shouldIncludeRunningBalance?: boolean; pageSize?: number; pageToken?: string }
): Promise<ApiResponse<GetAccountLedgerResponse>> {
  const { shouldIncludeRunningBalance, pageSize, pageToken } = options ?? {}
  const query = buildQueryString({
    ...buildPaginationParams(pageSize, pageToken),
    ...buildDateRangeParams(filter?.startDate, filter?.endDate),
    includeRunningBalance: shouldIncludeRunningBalance,
  });
  return get<GetAccountLedgerResponse>(
    `/organizations/${orgId}/accounts/${accountId}/ledger${query}`
  );
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if a journal entry is balanced.
 */
export function isBalanced(entry: JournalEntry): boolean {
  return entry.totalDebit.amount === entry.totalCredit.amount;
}

/**
 * Calculate the total debits from lines.
 */
export function calculateTotalDebits(lines: CreateJournalEntryLine[]): number {
  return calcDebits(lines);
}

/**
 * Calculate the total credits from lines.
 */
export function calculateTotalCredits(lines: CreateJournalEntryLine[]): number {
  return calcCredits(lines);
}

/**
 * Validate journal entry lines before submission.
 */
export function validateLines(lines: CreateJournalEntryLine[]): string[] {
  return validateJournalEntryLines(lines);
}

/**
 * Get status badge color.
 */
export function getStatusColor(status: EntryStatus): string {
  return ENTRY_STATUS_COLORS[status] ?? 'gray';
}

/**
 * Get entry type label.
 */
export function getEntryTypeLabel(type: EntryType): string {
  return getLabel(type, ENTRY_TYPE_LABELS);
}
